import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const SalesVisitsList = () => {
  const [salesVisits, setSalesVisits] = useState([]);
  const [filteredSalesVisits, setFilteredSalesVisits] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch staff list
        const staffResponse = await fetch(`${BASE_URL}staff`);
        const staffData = await staffResponse.json();

        let staffListData = [];
        if (staffData.status === 1 && staffData.data) {
          staffListData = staffData.data;
          setStaffList(staffListData);
        }

        // Fetch sales visits
        const salesResponse = await fetch(`${BASE_URL}salesvisits`);
        const salesData = await salesResponse.json();

        if (salesData.status === 1 && salesData.data) {
          // Map staff names to sales visits
          const visitsWithStaffNames = salesData.data.map((visit) => {
            const staff = staffListData.find((s) => s.id == visit.staff_id);
            return {
              ...visit,
              staff_name: staff ? staff.name : `Staff ID: ${visit.staff_id}`,
            };
          });

          setSalesVisits(visitsWithStaffNames);
          setFilteredSalesVisits(visitsWithStaffNames);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get staff name by ID
  const getStaffName = (staffId) => {
    if (!staffId || !staffList.length) return "N/A";
    const staff = staffList.find((s) => s.id == staffId);
    return staff ? staff.name : `Staff ID: ${staffId}`;
  };

  // Convert ISO Date to yyyy-mm-dd
  const formatOnlyDate = (isoDate) => {
    if (!isoDate) return "";
    const d = new Date(isoDate);
    return d.toISOString().split("T")[0];
  };

  // Format date for display
  const formatDisplayDate = (isoDate) => {
    if (!isoDate) return "N/A";
    const d = new Date(isoDate);
    return d.toLocaleDateString("en-GB");
  };

  // Total PCS Calculation
  const calculateTotalPCS = () => {
    return filteredSalesVisits.reduce(
      (total, visit) => total + (visit.sales || 0),
      0
    );
  };

  // Search + Date Filter Combined
  const filterData = (term, start, end) => {
    const termLower = term.toLowerCase();
    const filtered = salesVisits.filter((visit) => {
      const visitDate = formatOnlyDate(visit.date);
      const startD = start ? start : null;
      const endD = end ? end : null;

      // Date filtering
      if (startD && visitDate < startD) return false;
      if (endD && visitDate > endD) return false;

      // Search Filter including staff name
      return (
        visit.name?.toLowerCase().includes(termLower) ||
        visit.area?.toLowerCase().includes(termLower) ||
        visit.city?.toLowerCase().includes(termLower) ||
        visit.description?.toLowerCase().includes(termLower) ||
        visit.staff_name?.toLowerCase().includes(termLower) ||
        visit.staff_id?.toString().includes(term)
      );
    });

    setFilteredSalesVisits(filtered);
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    filterData(term, startDate, endDate);
  };

  const handleDateFilter = () => {
    filterData(searchTerm, startDate, endDate);
  };

  const handleAdd = () => {
    navigate("/AddSalesVisit");
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this sales visit?")) {
      fetch(`${BASE_URL}salesvisits/${id}`, {
        method: "DELETE",
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 1) {
            setSalesVisits(salesVisits.filter((v) => v.id !== id));
            setFilteredSalesVisits(
              filteredSalesVisits.filter((v) => v.id !== id)
            );
          }
        })
        .catch((error) => console.error("Error deleting:", error));
    }
  };

  // PDF Export with staff name
  const handleExportPDF = () => {
    const doc = new jsPDF();

    doc.text("Sales Visits List", 14, 10);
    const totalPCS = calculateTotalPCS();
    doc.text(`Total PCS of Sale: ${totalPCS}`, 14, 20);

    const tableData = filteredSalesVisits.map((visit) => [
      visit.staff_name || getStaffName(visit.staff_id) || "N/A",
      visit.name || "N/A",
      visit.area || "N/A",
      visit.city || "N/A",
      visit.sales || "N/A",
      visit.description || "N/A",
      formatDisplayDate(visit.date),
    ]);

    doc.autoTable({
      head: [
        [
          "Staff Name",
          "Firm Name",
          "Area",
          "City",
          "PCS",
          "Description",
          "Date",
        ],
      ],
      body: tableData,
      startY: 30,
    });

    doc.save("sales_visits_list.pdf");
  };

  // Excel Export
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredSalesVisits.map((visit) => ({
        "Staff Name": visit.staff_name || getStaffName(visit.staff_id) || "N/A",
        "Firm Name": visit.name || "N/A",
        Area: visit.area || "N/A",
        City: visit.city || "N/A",
        PCS: visit.sales || "N/A",
        Description: visit.description || "N/A",
        Date: formatDisplayDate(visit.date),
        Image: visit.image ? `https://pbhrm.com${visit.image}` : "No Image",
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Visits");
    XLSX.writeFile(workbook, "sales_visits_list.xlsx");
  };

  if (isLoading) {
    return (
      <div id="main">
        <div className="page-heading">
          <h3>Sales Visits List</h3>
        </div>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-3">Loading sales visits...</span>
        </div>
      </div>
    );
  }

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6">
              <h3>Sales Visits List</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-6">
                    <h5>Total PCS of Sale: {calculateTotalPCS()}</h5>
                  </div>

                  <div className="col-md-6 text-end">
                    <button
                      className="btn btn-primary me-2"
                      onClick={handleAdd}
                    >
                      Add Sales Visit
                    </button>
                    <button
                      className="btn btn-secondary me-2"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={handleExportExcel}
                    >
                      Export to Excel
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body">
                {/* Search */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Search</label>
                    <input
                      type="text"
                      className="form-control"
                      value={searchTerm}
                      placeholder="Staff / Firm / Area / City / Description"
                      onChange={handleSearchChange}
                    />
                  </div>

                  {/* Date Filters */}
                  <div className="col-md-4">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  className="btn btn-secondary mb-3"
                  onClick={handleDateFilter}
                >
                  Apply Date Filter
                </button>

                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>Staff Name</th>
                        <th>Firm Name</th>
                        <th>Area</th>
                        <th>City</th>
                        <th>PCS</th>
                        <th>Description</th>
                        <th>Image</th>
                        <th>Date</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredSalesVisits.length > 0 ? (
                        filteredSalesVisits.map((visit) => (
                          <tr key={visit.id}>
                            <td>
                              {visit.staff_name ||
                                getStaffName(visit.staff_id) ||
                                "N/A"}
                            </td>
                            <td>{visit.name}</td>
                            <td>{visit.area}</td>
                            <td>{visit.city}</td>
                            <td>{visit.sales}</td>
                            <td>{visit.description}</td>
                            <td>
                              {visit.image ? (
                                <img
                                  src={`https://pbhrm.com${visit.image}`}
                                  alt="visit"
                                  style={{
                                    width: "70px",
                                    height: "70px",
                                    objectFit: "cover",
                                    borderRadius: "5px",
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src =
                                      "https://via.placeholder.com/70x70?text=No+Image";
                                  }}
                                />
                              ) : (
                                <span>No Image</span>
                              )}
                            </td>
                            <td>{formatDisplayDate(visit.date)}</td>

                            <td className="text-end">
                              <button
                                className="btn btn-danger"
                                onClick={() => handleDelete(visit.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center">
                            No sales visits found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalesVisitsList;
